import React, { useState } from 'react';
import { useOpenAI } from '../../hooks/useOpenAI';
import Icon from '../AppIcon';
import Button from '../ui/Button';

const ProductAnalysisPanel = ({ 
  className = '',
  productData = null,
  onAnalysisComplete = null 
}) => {
  const [analysisResult, setAnalysisResult] = useState(null);
  const [inputData, setInputData] = useState('');

  const { 
    loading,
    error,
    productMatching,
    categorizeProductData,
    generateDescription,
    clearError
  } = useOpenAI();

  const handleAnalyzeProduct = async () => {
    const dataToAnalyze = productData || inputData;
    if (!dataToAnalyze?.trim()) return;

    clearError();
    
    try {
      const [matchingAnalysis, categorization] = await Promise.all([
        productMatching(dataToAnalyze),
        categorizeProductData(dataToAnalyze)
      ]);

      const result = {
        matching: matchingAnalysis,
        categorization: categorization,
        timestamp: new Date()
      };

      setAnalysisResult(result);
      
      if (onAnalysisComplete) {
        onAnalysisComplete(result);
      }
    } catch (err) {
      console.error('Analysis failed:', err);
    }
  };

  const handleGenerateDescription = async () => {
    const dataToAnalyze = productData || inputData;
    if (!dataToAnalyze?.trim()) return;

    try {
      const description = await generateDescription({ productData: dataToAnalyze });
      
      setAnalysisResult(prev => ({
        ...prev,
        generatedDescription: description,
        timestamp: new Date()
      }));
    } catch (err) {
      console.error('Description generation failed:', err);
    }
  };

  const resetAnalysis = () => {
    setAnalysisResult(null);
    setInputData('');
    clearError();
  };

  return (
    <div className={`bg-card border border-border rounded-lg p-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
            <Icon name="Search" size={20} color="white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">AI Product Analysis</h3>
            <p className="text-sm text-muted-foreground">
              Get AI-powered insights for product matching and categorization
            </p>
          </div>
        </div>
        {analysisResult && (
          <Button variant="ghost" size="sm" onClick={resetAnalysis}>
            <Icon name="RotateCcw" size={16} />
            Reset
          </Button>
        )}
      </div>

      {/* Input Section - Only show if no productData prop */}
      {!productData && (
        <div className="mb-6">
          <label className="block text-sm font-medium text-foreground mb-2">
            Product Data
          </label>
          <textarea
            value={inputData}
            onChange={(e) => setInputData(e?.target?.value)}
            placeholder="Enter product name, description, specifications, or any relevant data..."
            className="w-full h-32 rounded-md border border-border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
            disabled={loading}
          />
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-3 mb-6">
        <Button
          onClick={handleAnalyzeProduct}
          disabled={loading || (!productData && !inputData?.trim())}
          className="flex items-center space-x-2"
        >
          {loading ? (
            <Icon name="Loader2" size={16} className="animate-spin" />
          ) : (
            <Icon name="Search" size={16} />
          )}
          <span>Analyze Product</span>
        </Button>

        <Button
          variant="outline"
          onClick={handleGenerateDescription}
          disabled={loading || (!productData && !inputData?.trim())}
          className="flex items-center space-x-2"
        >
          {loading ? (
            <Icon name="Loader2" size={16} className="animate-spin" />
          ) : (
            <Icon name="FileText" size={16} />
          )}
          <span>Generate Description</span>
        </Button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
          <div className="flex items-center space-x-2 text-destructive">
            <Icon name="AlertCircle" size={16} />
            <span className="text-sm">{error}</span>
            <Button variant="ghost" size="sm" onClick={clearError} className="ml-auto">
              <Icon name="X" size={14} />
            </Button>
          </div>
        </div>
      )}

      {/* Analysis Results */}
      {analysisResult && (
        <div className="space-y-6">
          {/* Product Matching Analysis */}
          {analysisResult?.matching && (
            <div className="border border-border rounded-lg p-4">
              <h4 className="flex items-center space-x-2 font-semibold text-foreground mb-3">
                <Icon name="Target" size={16} />
                <span>Product Matching Analysis</span>
              </h4>
              
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-foreground">Analysis</label>
                  <p className="text-sm text-muted-foreground mt-1">
                    {analysisResult?.matching?.analysis}
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium text-foreground">Confidence Score</label>
                  <div className="flex items-center space-x-2 mt-1">
                    <div className="flex-1 bg-muted rounded-full h-2">
                      <div 
                        className="bg-primary h-2 rounded-full transition-all duration-300"
                        style={{ width: `${(analysisResult?.matching?.confidence_score || 0) * 100}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium text-foreground">
                      {Math.round((analysisResult?.matching?.confidence_score || 0) * 100)}%
                    </span>
                  </div>
                </div>

                {analysisResult?.matching?.potential_matches?.length > 0 && (
                  <div>
                    <label className="text-sm font-medium text-foreground">Potential Matches</label>
                    <ul className="mt-1 space-y-1">
                      {analysisResult?.matching?.potential_matches?.map((match, index) => (
                        <li key={index} className="text-sm text-muted-foreground flex items-center space-x-2">
                          <Icon name="ArrowRight" size={12} />
                          <span>{match}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {analysisResult?.matching?.pricing_insights && (
                  <div>
                    <label className="text-sm font-medium text-foreground">Pricing Insights</label>
                    <p className="text-sm text-muted-foreground mt-1">
                      {analysisResult?.matching?.pricing_insights}
                    </p>
                  </div>
                )}

                {analysisResult?.matching?.recommendations?.length > 0 && (
                  <div>
                    <label className="text-sm font-medium text-foreground">Recommendations</label>
                    <ul className="mt-1 space-y-1">
                      {analysisResult?.matching?.recommendations?.map((rec, index) => (
                        <li key={index} className="text-sm text-muted-foreground flex items-center space-x-2">
                          <Icon name="CheckCircle" size={12} />
                          <span>{rec}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Categorization Results */}
          {analysisResult?.categorization && (
            <div className="border border-border rounded-lg p-4">
              <h4 className="flex items-center space-x-2 font-semibold text-foreground mb-3">
                <Icon name="Tag" size={16} />
                <span>Product Categorization</span>
              </h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-foreground">Primary Category</label>
                  <p className="text-sm text-muted-foreground mt-1">
                    {analysisResult?.categorization?.primary_category}
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium text-foreground">Subcategory</label>
                  <p className="text-sm text-muted-foreground mt-1">
                    {analysisResult?.categorization?.subcategory}
                  </p>
                </div>

                {analysisResult?.categorization?.tags?.length > 0 && (
                  <div className="md:col-span-2">
                    <label className="text-sm font-medium text-foreground">Tags</label>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {analysisResult?.categorization?.tags?.map((tag, index) => (
                        <span 
                          key={index}
                          className="px-2 py-1 bg-secondary text-secondary-foreground rounded-md text-xs"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {analysisResult?.categorization?.attributes && (
                  <div className="md:col-span-2">
                    <label className="text-sm font-medium text-foreground">Key Attributes</label>
                    <div className="mt-1 space-y-1">
                      {Object?.entries(analysisResult?.categorization?.attributes || {})?.map(([key, value]) => (
                        <div key={key} className="flex justify-between text-sm">
                          <span className="text-muted-foreground capitalize">{key?.replace('_', ' ')}:</span>
                          <span className="text-foreground">{value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Generated Description */}
          {analysisResult?.generatedDescription && (
            <div className="border border-border rounded-lg p-4">
              <h4 className="flex items-center space-x-2 font-semibold text-foreground mb-3">
                <Icon name="FileText" size={16} />
                <span>AI-Generated Description</span>
              </h4>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {analysisResult?.generatedDescription}
              </p>
            </div>
          )}

          {/* Analysis Timestamp */}
          <div className="text-xs text-muted-foreground text-center pt-4 border-t border-border">
            Analysis completed on {analysisResult?.timestamp?.toLocaleString()}
          </div>
        </div>
      )}

      {/* Empty State */}
      {!analysisResult && !loading && (
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="Search" size={24} color="currentColor" className="text-muted-foreground" />
          </div>
          <h4 className="text-lg font-medium text-foreground mb-2">Ready for Analysis</h4>
          <p className="text-sm text-muted-foreground">
            {productData 
              ? 'Click "Analyze Product" to get AI-powered insights for this product.' 
              : 'Enter product data above and click "Analyze Product" to get started.'
            }
          </p>
        </div>
      )}
    </div>
  );
};

export default ProductAnalysisPanel;